import { useState } from "react";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

const A_LEVEL = ["A*", "A", "B", "C", "D", "E"];
const field = "mt-1 w-full rounded-md border border-stone-300 bg-white px-2 py-1.5 text-sm font-mono focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4C1D95]";

function Select({ id, label, value, onChange, options }) {
  return (
    <label htmlFor={id} className="block text-[12px] text-stone-700">
      {label}
      <select id={id} data-testid={id} value={value} onChange={(e) => onChange(e.target.value)} className={field}>
        <option value="">Not entered</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}

function NumberField({ id, label, value, onChange, min, max, step = 1 }) {
  return (
    <label htmlFor={id} className="block text-[12px] text-stone-700">
      {label}
      <input id={id} data-testid={id} type="number" inputMode="decimal" min={min} max={max} step={step}
        value={value} onChange={(e) => onChange(e.target.value)} className={field} />
    </label>
  );
}

const inRange = (v, min, max) => v === "" || (Number(v) >= min && Number(v) <= max);

// Collects only the gap inputs that are blank, saves them as the student's own answers, then the match re-runs.
export function SecureEvidenceForm({ profile, blanks, onSaved }) {
  const cur = profile?.curriculum;
  const needSubjects = blanks.some((b) => b[0].startsWith("subject"));
  const needTest = blanks.some((b) => b[0].includes("admissions test"));
  const needEnglish = blanks.some((b) => b[0].includes("English test"));
  const [v, setV] = useState({ maths: "", english: "", testType: "sat", test: "", engType: "ielts", eng: "", waived: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (k) => (x) => setV((s) => ({ ...s, [k]: x }));

  const testRange = v.testType === "sat" ? [400, 1600] : [1, 36];
  const engRange = v.engType === "ielts" ? [0, 9] : [0, 120];
  const valid = inRange(v.test, ...testRange) && (v.waived || inRange(v.eng, ...engRange));

  const submit = async (e) => {
    e.preventDefault();
    const patch = {};
    if (needSubjects && v.maths) patch[cur === "ib" ? "ib_maths" : "a_level_maths"] = cur === "ib" ? Number(v.maths) : v.maths;
    if (needSubjects && v.english) patch[cur === "ib" ? "ib_english" : "a_level_english"] = cur === "ib" ? Number(v.english) : v.english;
    if (needTest && v.test !== "") patch[v.testType] = Number(v.test);
    if (needEnglish && v.waived) patch.english_waived = true;
    else if (needEnglish && v.eng !== "") patch[v.engType] = Number(v.eng);
    if (!Object.keys(patch).length) { setError("Enter at least one answer, or leave this for later."); return; }
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.post("/profile", { ...patch, explicit_fields: Object.keys(patch), source_surface: "advanced_secure_prompt" });
      onSaved(data?.profile || { ...profile, ...patch });
    } catch {
      setError("Couldn't save. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const ib = cur === "ib";
  const subjectOpts = ib ? ["7", "6", "5", "4", "3", "2", "1"] : A_LEVEL;
  return (
    <form data-testid="secure-evidence-form" onSubmit={submit} className="mt-3 rounded-lg border border-[#4C1D95]/25 bg-white p-3">
      <p className="text-[12px] text-stone-700">
        Add what you know. Your results will be recalculated and can go <strong>up or down</strong>; nothing is saved until you press Save.
      </p>
      <div className="mt-2 grid gap-3 sm:grid-cols-2">
        {needSubjects && (ib || cur === "a_level") && (
          <>
            <Select id="secure-maths" label={ib ? "IB Maths (HL/SL grade)" : "A-level Maths grade"} value={v.maths} onChange={set("maths")} options={subjectOpts} />
            <Select id="secure-english" label={ib ? "IB English grade" : "A-level English grade"} value={v.english} onChange={set("english")} options={subjectOpts} />
          </>
        )}
        {needTest && (
          <div className="grid grid-cols-[auto_1fr] gap-2 items-end">
            <Select id="secure-test-type" label="Admissions test" value={v.testType} onChange={set("testType")} options={["sat", "act"]} />
            <NumberField id="secure-test" label={v.testType === "sat" ? "SAT score (400–1600)" : "ACT score (1–36)"} value={v.test}
              onChange={set("test")} min={testRange[0]} max={testRange[1]} />
          </div>
        )}
        {needEnglish && (
          <div className="space-y-2">
            {!v.waived && (
              <div className="grid grid-cols-[auto_1fr] gap-2 items-end">
                <Select id="secure-eng-type" label="English test" value={v.engType} onChange={set("engType")} options={["ielts", "toefl"]} />
                <NumberField id="secure-eng" label={v.engType === "ielts" ? "IELTS overall (0–9)" : "TOEFL iBT (0–120)"} value={v.eng}
                  onChange={set("eng")} min={engRange[0]} max={engRange[1]} step={v.engType === "ielts" ? 0.5 : 1} />
              </div>
            )}
            <label className="flex items-center gap-2 text-[12px] text-stone-700">
              <input type="checkbox" data-testid="secure-waived" checked={v.waived} onChange={(e) => set("waived")(e.target.checked)} className="accent-[#4C1D95]" />
              I'm a native English speaker or exempt from an English test
            </label>
          </div>
        )}
      </div>
      {!valid && <p role="alert" data-testid="secure-form-range" className="mt-2 text-[12px] text-rose-700">Check the score is within the range shown.</p>}
      {error && <p role="alert" data-testid="secure-form-error" className="mt-2 text-[12px] text-rose-700">{error}</p>}
      <Button type="submit" size="sm" disabled={busy || !valid} data-testid="secure-form-save" className="mt-3 bg-[#4C1D95] hover:bg-[#3b1479] text-white">
        {busy && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" aria-hidden="true" />} Save and recalculate
      </Button>
    </form>
  );
}
